/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Component } from '@appCanvasCommon/component-system/Component';
import { ComponentInterface } from '@appCanvasCommon/interfaces/component.interface';
import { CanvasAngularLand } from '../../../../app-data/interfaces/canvas-angular-land';
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { ExportCapture, ICapturedTile } from '@appCanvasCommon/export/ExportCapture';
import { IPrintConfiguration } from '../../../../app-data/constants/plan-print.model';
import { Lightbox } from '@appCanvasCommon/export/Lightbox';
import { IExportRect } from '@appPlanApplication/export/FancyPrint';
import { ExportStitch, IStitchResult } from '@appCanvasCommon/export/ExportStitch';

const settings = {
	xOffset: 0,
	yOffset: 100,
};

export class CrewSizeDrawer extends Component implements ComponentInterface {
	/**
	 * Creates a new ExportStitcher instance.
	 * @returns A new ExportStitcher instance
	 * @protected
	 */
	protected createExportStitch(): ExportStitch {
		return new ExportStitch();
	}
	/**
	 * Invoked at the start of the stitching process. Returns a ICapturedTile that can be used by the stitcher.
	 * @param canvasAngularLand - Angular dependencies, etc
	 * @param application - The AbstractApplication this is attached to
	 * @param lightbox - The rectangle representing the export region
	 * @param config - The current Print Configuration used for the capture
	 * @param exportRect - The rectangle of the final export
	 * @returns ICapturedTile - Returns a ICapturedTile object that can be consumed by the ExportStitcher
	 */
	public onExportDrawNode(
		canvasAngularLand: CanvasAngularLand,
		application: AbstractApplication,
		lightbox: Lightbox,
		config: IPrintConfiguration,
		exportRect: IExportRect
	): ICapturedTile {
		const stitcher: ExportStitch = this.createExportStitch();
		const tiles: ICapturedTile[] = canvasAngularLand.crewSizeService.getCapturedTiles();
		const exporter: ExportCapture | null = canvasAngularLand.crewSizeService.exporter;
		if (!exporter || !exporter.tiles.length) {
			return ExportCapture.createEmptyTile();
		}

		const width: number = exporter.width;
		const height: number = exporter.height;
		const result: IStitchResult = stitcher.stitch(tiles, width, height);
		if (!result.success) {
			return ExportCapture.createEmptyTile();
		}

		return {
			canvas: result.canvas as HTMLCanvasElement,
			position: {
				x: settings.xOffset,
				y: settings.yOffset,
				width,
				height,
			},
			index: 0,
		};
	}

	/**
	 * Invoked once the export process is completed.
	 * @param canvasAngularLand
	 * @param application
	 */
	public onExportComplete(canvasAngularLand: CanvasAngularLand, application: AbstractApplication): void {
		const exporter: ExportCapture | null = canvasAngularLand.crewSizeService.exporter;
		if (!exporter) {
			return;
		}
		exporter.purge();
	}
}
